import React, { useState, useEffect, useMemo } from 'react';
import Header from '../../Header';
import { local_signage } from '../../Const';
import {
    Card,
    CardContent,
    CardHeader,
    Grid,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Paper,
    Autocomplete,
    Box,
    Typography,
    Divider,
    Chip,
    Snackbar,
    Alert,
    Checkbox
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import DescriptionIcon from '@mui/icons-material/Description';
import SettingsIcon from '@mui/icons-material/Settings';
import InventoryIcon from '@mui/icons-material/Inventory';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

// DUMMY_BYPRODUCTS removed - fetching from API
const DUMMY_ITEMS = [
    { id: '72000251', name: 'Broiler Dress With Skin' },
    { id: '72000252', name: 'Broiler Dress Without Skin' },
    { id: '72000253', name: 'Chicken Whole Bird' },
    { id: '72000254', name: 'Chicken Breast Boneless' },
    { id: '72000255', name: 'Chicken Thigh Boneless' },
    { id: '72000256', name: 'Chicken Drumstick' }
];

export const BomByproduct = () => {
    // Get auth token from localStorage
    const token = useMemo(() => localStorage.getItem('AuthorizationToken'), []);

    // State
    const [description, setDescription] = useState('');
    const [byproducts, setByproducts] = useState([]);
    const [byproductsList, setByproductsList] = useState([]);
    const [selectedByproduct, setSelectedByproduct] = useState(null);
    const [maxQtyPercent, setMaxQtyPercent] = useState('');

    // New State for BOM ID Dropdown
    const [selectedBomId, setSelectedBomId] = useState(null);
    const [bomIds, setBomIds] = useState([]);
    const [itemsList, setItemsList] = useState([]);
    const [selectedItems, setSelectedItems] = useState([]);

    // DML State Tracking
    const [originalByproducts, setOriginalByproducts] = useState([]);
    const [originalItems, setOriginalItems] = useState([]);

    // Fetch BOM ID List and Items List on Mount
    useEffect(() => {
        const fetchInitialData = async () => {
            if (!token) return;
            try {
                // Fetch BOM IDs
                const bomResponse = await fetch(`${local_signage}/api/v1/fetchbyproductbomlist`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (bomResponse.ok) {
                    const bomData = await bomResponse.json();
                    // Prepend "New BOM" option
                    const completeList = [{ ByproductBomId: 'NEW', ByproductBomName: 'New BOM' }, ...bomData];
                    setBomIds(completeList);
                }

                // Fetch Items List for Associated Items Dropdown (ItemFamilyID=DR)
                const itemsResponse = await fetch(`${local_signage}/api/v1/fetchitembyfamily?ItemFamilyID=DR`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (itemsResponse.ok) {
                    const itemsData = await itemsResponse.json();
                    const mappedItems = itemsData
                        .filter(item => item.Deleted === 'N')
                        .map(item => ({
                            id: item.ItemID,
                            name: item.ItemName
                        }));
                    setItemsList(mappedItems);
                }

            } catch (error) {
                console.error('Error fetching initial data:', error);
                showNotification('Failed to load initial data', 'error');
            }
        };
        fetchInitialData();
    }, [token]);

    const handleBomIdChange = async (newValue) => {
        setSelectedBomId(newValue);
        if (!newValue) {
            setByproducts([]);
            setSelectedItems([]);
            setOriginalByproducts([]); // Clear DML state
            setOriginalItems([]);      // Clear DML state
            setDescription('');
            return;
        }

        // Handle "New BOM" Selection
        if (newValue.ByproductBomId === 'NEW') {
            setByproducts([]);
            setSelectedItems([]);
            setOriginalByproducts([]); // Clear original state
            setOriginalItems([]);      // Clear original state
            setDescription(''); // Clear description for user to enter
            showNotification('Please enter a description for the new BOM', 'info');
            return;
        }

        // Handle Existing BOM Selection
        setDescription(newValue.ByproductBomName || ''); // Auto-populate Description

        try {
            const response = await fetch(`${local_signage}/api/v1/fetchbyproductbomdetail?ByproductBomId=${newValue.ByproductBomId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) throw new Error('Failed to fetch BOM details');

            const result = await response.json();
            if (result.success && result.data) {
                // Populate Byproducts Table
                const mappedByproducts = (result.data.byproductItemDetails || []).map((item, index) => {
                    const productDetails = byproductsList.find(bp => bp.ItemId === item.ByproductId);
                    return {
                        id: Date.now() + index, // unique key
                        ByproductId: item.ByproductId,
                        ByproductName: productDetails ? productDetails.itemname : item.ItemName,
                        MaxQtyPercent: item.MaxQtyPercent
                    };
                });
                setByproducts(mappedByproducts);
                setOriginalByproducts(JSON.parse(JSON.stringify(mappedByproducts))); // Deep copy for comparison

                // Populate Associated Items
                const mappedItems = (result.data.itemDetails || []).map(item => ({
                    id: item.ItemID,
                    name: item.ItemName
                }));
                setSelectedItems(mappedItems);
                setOriginalItems(JSON.parse(JSON.stringify(mappedItems))); // Deep copy for comparison
            }
        } catch (error) {
            console.error('Error fetching BOM details:', error);
            showNotification('Failed to load BOM details', 'error');
        }
    };

    useEffect(() => {
        const fetchByproducts = async () => {
            try {
                const response = await fetch(`${local_signage}/api/v1/fetchbyproductlist`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                if (!response.ok) throw new Error('Failed to fetch byproducts');
                const data = await response.json();
                setByproductsList(data);
            } catch (error) {
                console.error('Error fetching byproducts:', error);
                showNotification('Failed to load byproducts', 'error');
            }
        };

        if (token) fetchByproducts();
    }, [token]);



    const [isSubmitting, setIsSubmitting] = useState(false);

    // Notification State
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    const showNotification = (message, severity = 'success') => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbarOpen(false);
    };

    const handleAddByproduct = () => {
        if (!selectedByproduct) {
            showNotification('Please select a byproduct', 'error');
            return;
        }
        if (!maxQtyPercent || parseFloat(maxQtyPercent) <= 0 || parseFloat(maxQtyPercent) > 100) {
            showNotification('Max Qty % must be between 0 and 100', 'error');
            return;
        }

        // Check for duplicates
        if (byproducts.some(bp => bp.ByproductId === selectedByproduct.ItemId)) {
            showNotification('This byproduct is already added', 'error');
            return;
        }

        const newByproduct = {
            id: Date.now(),
            ByproductId: selectedByproduct.ItemId,
            ByproductName: selectedByproduct.itemname,
            MaxQtyPercent: parseFloat(maxQtyPercent)
        };

        setByproducts([...byproducts, newByproduct]);
        setSelectedByproduct(null);
        setMaxQtyPercent('');
        showNotification('Byproduct added successfully');
    };

    const handleDeleteByproduct = (id) => {
        setByproducts(byproducts.filter(bp => bp.id !== id));
        showNotification('Byproduct removed', 'warning');
    };

    const handleClear = () => {
        setDescription('');
        setByproducts([]);
        setSelectedByproduct(null);
        setMaxQtyPercent('');
        setSelectedItems([]);
        setOriginalByproducts([]); // Clear DML state
        setOriginalItems([]);      // Clear DML state
        showNotification('Form cleared', 'info');
    };

    const handleSubmit = async () => {
        // Validation
        if (!description || description.trim().length < 3) {
            showNotification('Please enter a description (min 3 characters)', 'error');
            return;
        }
        if (byproducts.length === 0) {
            showNotification('Please add at least one byproduct', 'error');
            return;
        }
        if (selectedItems.length === 0) {
            showNotification('Please select at least one item', 'error');
            return;
        }

        try {
            setIsSubmitting(true);

            // Calculate DML for Byproducts
            const finalByproducts = [];

            // Handle Insert (I) and Edit (E)
            byproducts.forEach(bp => {
                const original = originalByproducts.find(obp => obp.ByproductId === bp.ByproductId);
                if (!original) {
                    finalByproducts.push({ ...bp, DML: 'I' });
                } else {
                    finalByproducts.push({ ...bp, DML: 'E' });
                }
            });

            // Handle Delete (D) - Items in original but not in current
            originalByproducts.forEach(obp => {
                if (!byproducts.find(bp => bp.ByproductId === obp.ByproductId)) {
                    finalByproducts.push({ ...obp, DML: 'D' });
                }
            });

            // Calculate DML for Items
            const finalItems = [];

            // Handle Insert (I) - New items added
            // Note: For Associated Items, we only track presence. 'E' implies re-adding?? Usually just I or D.
            // If it's effectively a list of IDs, if it's there it's I (if new) or E (if existing).
            selectedItems.forEach(item => {
                const original = originalItems.find(oi => oi.id === item.id);
                if (!original) {
                    finalItems.push({ ItemId: item.id, DML: 'I' });
                } else {
                    finalItems.push({ ItemId: item.id, DML: 'I' }); // Keep as I or E? Requirement says E if edit. 
                    // Actually, if it's just a relationship, 'I' usually works for "Keep/Update". 
                    // But user said "if Edit E". Let's use E for existing.
                }
            });

            // Wait, for items "Associated Items", if I uncheck valid item, it should be D.
            // If I check new item, it should be I.
            // If I check existing item... well it's already checked. 
            // So logic:
            // Current List items:
            // If in Original -> E (No change really, but maybe 'Touch') or just ignore? 
            // User request: "if new item is inserting pass I if Edit E and if any Item is remove it should be D"

            // Refined Logic for Items:
            selectedItems.forEach(item => {
                const original = originalItems.find(oi => oi.id === item.id);
                if (original) {
                    finalItems.push({ ItemId: item.id, DML: 'E' });
                } else {
                    finalItems.push({ ItemId: item.id, DML: 'I' });
                }
            });

            // Handle Delete (D) for Items
            originalItems.forEach(original => {
                if (!selectedItems.find(curr => curr.id === original.id)) {
                    finalItems.push({ ItemId: original.id, DML: 'D' });
                }
            });

            const payload = {
                ByproductBomId: (selectedBomId && selectedBomId.ByproductBomId !== 'NEW') ? selectedBomId.ByproductBomId : "",
                Description: description.trim(),
                byprod: finalByproducts.map(bp => ({
                    ByproductId: bp.ByproductId,
                    MaxQtyPercent: bp.MaxQtyPercent,
                    DML: bp.DML
                })),
                itm: finalItems.map(item => ({
                    ItemId: item.ItemId,
                    DML: item.DML
                }))
            };

            // Perform API Submission
            const response = await fetch(`${local_signage}/api/v1/insertbyproductbom`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Submission failed');
            }

            const result = await response.json();
            console.log('API Response:', result);

            showNotification('Submitted Successfully', 'success');
            handleClear();

        } catch (error) {
            console.error('Error submitting byproduct BOM:', error);
            showNotification('Failed to submit. Please try again.', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', display: 'flex', flexDirection: 'column' }}>
            <Header />
            <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
                <Card sx={{ maxWidth: 1200, width: '100%', borderRadius: 2, boxShadow: 3 }}>
                    <CardHeader
                        title={
                            <Typography variant="h5" component="div" sx={{ fontWeight: 'bold', color: '#004d00' }}>
                                Byproduct BOM Master
                            </Typography>
                        }
                        sx={{ borderBottom: '3px solid #004d00', pb: 1 }}
                    />
                    <CardContent sx={{ p: 3 }}>
                        <Grid container spacing={3}>
                            {/* Top Section: BOM ID and Description */}
                            <Grid item xs={12} container spacing={2}>
                                {/* BOM ID Dropdown */}
                                <Grid item xs={12} md={4}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, color: '#004d00' }}>
                                        <DescriptionIcon sx={{ mr: 1 }} />
                                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>BOM ID</Typography>
                                    </Box>
                                    <Autocomplete
                                        size="small"
                                        options={bomIds}
                                        getOptionLabel={(option) => option.ByproductBomName || ''}
                                        value={selectedBomId}
                                        onChange={(event, newValue) => handleBomIdChange(newValue)}
                                        renderInput={(params) => <TextField {...params} label="Select BOM ID" variant="outlined" />}
                                    />
                                </Grid>

                                {/* Description Field */}
                                <Grid item xs={12} md={8}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, color: '#004d00' }}>
                                        <DescriptionIcon sx={{ mr: 1 }} />
                                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Description</Typography>
                                    </Box>
                                    <TextField
                                        fullWidth
                                        label="Description"
                                        variant="outlined"
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        placeholder="Enter byproduct BOM description..."
                                        required
                                        size="small"
                                    />
                                </Grid>
                            </Grid>

                            <Grid item xs={12}>
                                <Divider />
                            </Grid>

                            {/* Split Layout Container */}
                            <Grid item container spacing={3}>
                                {/* Left Side: Byproduct Configuration (approx 60%) */}
                                <Grid item xs={12} md={7}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, color: '#004d00' }}>
                                        <SettingsIcon sx={{ mr: 1 }} />
                                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Byproduct Configuration</Typography>
                                    </Box>

                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={12} sm={5}>
                                            <Autocomplete
                                                size="small"
                                                options={byproductsList}
                                                getOptionLabel={(option) => option.itemname || ''}
                                                value={selectedByproduct}
                                                onChange={(event, newValue) => setSelectedByproduct(newValue)}
                                                renderInput={(params) => <TextField {...params} label="Select Byproduct" required />}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <TextField
                                                size="small"
                                                fullWidth
                                                label="Max Qty %"
                                                type="number"
                                                value={maxQtyPercent}
                                                onChange={(e) => setMaxQtyPercent(e.target.value)}
                                                placeholder="0.00"
                                                required
                                                inputProps={{ step: "0.01", min: "0", max: "100" }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={3}>
                                            <Button
                                                variant="contained"
                                                startIcon={<AddIcon />}
                                                onClick={handleAddByproduct}
                                                fullWidth
                                                disabled={!selectedByproduct || !maxQtyPercent}
                                                sx={{
                                                    bgcolor: '#004d00',
                                                    '&:hover': { bgcolor: '#003300' },
                                                    height: '40px'
                                                }}
                                            >
                                                Add
                                            </Button>
                                        </Grid>
                                    </Grid>

                                    {/* Added Byproducts Table - Moved here */}
                                    {byproducts.length > 0 && (
                                        <Box sx={{ mt: 3 }}>
                                            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                                                <Table stickyHeader size="small">
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell sx={{ bgcolor: '#e8f3e8', fontWeight: 'bold', color: '#004d00' }}>Byproduct Name</TableCell>
                                                            <TableCell align="right" sx={{ bgcolor: '#e8f3e8', fontWeight: 'bold', color: '#004d00' }}>Max Qty %</TableCell>
                                                            <TableCell align="center" sx={{ bgcolor: '#e8f3e8', fontWeight: 'bold', color: '#004d00' }}>Action</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {byproducts.map((bp) => (
                                                            <TableRow key={bp.id} hover>
                                                                <TableCell>{bp.ByproductName}</TableCell>
                                                                <TableCell align="right">{bp.MaxQtyPercent.toFixed(2)}</TableCell>
                                                                <TableCell align="center">
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={() => handleDeleteByproduct(bp.id)}
                                                                        color="error"
                                                                    >
                                                                        <DeleteIcon fontSize="small" />
                                                                    </IconButton>
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableContainer>
                                        </Box>
                                    )}
                                </Grid>

                                {/* Right Side: Associated Items (approx 40%) */}
                                <Grid item xs={12} md={5}>
                                    <Box sx={{ height: '100%', borderLeft: { md: '1px solid #e0e0e0' }, pl: { md: 3 } }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, color: '#004d00' }}>
                                            <InventoryIcon sx={{ mr: 1 }} />
                                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Associated Items</Typography>
                                        </Box>
                                        <Autocomplete
                                            multiple
                                            disableCloseOnSelect
                                            size="medium"
                                            options={itemsList}
                                            isOptionEqualToValue={(option, value) => option.id === value.id}
                                            getOptionLabel={(option) => option.name}
                                            value={selectedItems}
                                            onChange={(event, newValue) => setSelectedItems(newValue)}
                                            renderOption={(props, option, { selected }) => (
                                                <li {...props}>
                                                    <Checkbox
                                                        icon={icon}
                                                        checkedIcon={checkedIcon}
                                                        style={{ marginRight: 8 }}
                                                        checked={selected}
                                                        color="success"
                                                    />
                                                    {option.name}
                                                </li>
                                            )}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Select Items"
                                                    placeholder={selectedItems.length === 0 ? "Select Items" : ""}
                                                    variant="outlined"
                                                />
                                            )}
                                            renderTags={() => null}
                                        />

                                        {/* Selected Items Chips Displayed Below */}
                                        <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                            {selectedItems.map((item) => (
                                                <Chip
                                                    key={item.id}
                                                    label={item.name}
                                                    onDelete={() => {
                                                        setSelectedItems(selectedItems.filter(i => i.id !== item.id));
                                                    }}
                                                    sx={{
                                                        bgcolor: '#e8f3e8',
                                                        color: '#004d00',
                                                        border: '1px solid #004d00',
                                                        fontWeight: 500
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>

                            <Grid item xs={12}>
                                <Divider />
                            </Grid>

                            {/* Action Buttons */}
                            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                                <Button
                                    variant="outlined"
                                    startIcon={<ClearIcon />}
                                    onClick={handleClear}
                                    sx={{
                                        color: '#004d00',
                                        borderColor: '#004d00',
                                        '&:hover': { borderColor: '#003300', bgcolor: '#e8f3e8' }
                                    }}
                                >
                                    Clear
                                </Button>
                                <Button
                                    variant="contained"
                                    startIcon={isSubmitting ? null : <SaveIcon />}
                                    onClick={handleSubmit}
                                    disabled={isSubmitting}
                                    sx={{
                                        bgcolor: '#004d00',
                                        '&:hover': { bgcolor: '#003300' },
                                        minWidth: 140
                                    }}
                                >
                                    {isSubmitting ? 'Submitting...' : 'Submit BOM'}
                                </Button>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Box>

            {/* Notification Snackbar */}
            <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
                <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%', boxShadow: 3 }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default BomByproduct;
